import React, { useEffect, useMemo, useState } from 'react'
import cx from 'classnames'
import { useDevice } from 'device'
import { type ModalComponentProps } from 'modals'
import { track } from 'analytics'
import localStorage from 'local-storage'
import { constants } from 'helpers'
import { useUser } from 'modules/user'
import { useCatalogueId, useCatalogueProducts, useProducts } from 'modules/products'

import { ModalButtons, ModalHeadline, PlainModal } from 'components/feedback'
import { buttonMessages } from 'components/inputs'

import ProductCardSmallSkeleton from 'compositions/productCards/ProductCardSmallSkeleton/ProductCardSmallSkeleton'
import ProductCardSmall from 'compositions/productCards/ProductCardSmall/ProductCardSmall'

import messages from './messages'
import s from './FirstProductModal.scss'


type FirstProductModalProps = {
  selectedTradingItemId: number
  onContinue: (product: ProductFragment.Base) => void
}


export const FirstProductModalView: React.FC<FirstProductModalProps & Pick<ModalComponentProps, 'closeModal'>> = (props) => {
  const { selectedTradingItemId, onContinue, closeModal } = props
  const { isMobile } = useDevice()

  const [ selectedItem, setSelectedItem ] = useState<ProductFragment.Base>(null)

  const { gender } = useUser()

  // TODO use picked items or recommendations in future - added on 2021-10-25 by maddoger
  const { catalogueId, isFetching: isCatalogueFetching } = useCatalogueId({ catalogueName: gender === 'FEMALE' ? 'perfumes' : 'colognes' })
  const { data, isFetching: isProductsFetching } = useCatalogueProducts({
    variables: {
      id: catalogueId,
      input: {
        filter: [ { type: 'CATEGORY', filterValueIdList: [ 'bestsellers' ] } ],
        offset: 0,
        limit: 12,
        productsFeed: 'BESTSELLERS',
      },
    },
    skip: !catalogueId,
  })

  // we use all saved sources of products
  const postponedItems = localStorage.getItem<QueueModule.AddItemsInput>(constants.localStorageNames.queuePendingProducts)
  const highPriorityProducts = localStorage.getItem<QueueModule.AddItemsInput>(constants.localStorageNames.queueHighPriorityProducts)
  const lowPriorityProducts = localStorage.getItem<QueueModule.AddItemsInput>(constants.localStorageNames.queueLowPriorityProducts)

  const postponedProductsIds = ([] as QueueModule.AddItemsInput).concat(highPriorityProducts, postponedItems, lowPriorityProducts)
    .filter(Boolean)
    .map((item) => String(item.product.productId))

  const { products: postponedProducts, isFetching: isPostponedProductsFetching } = useProducts({
    variables: {
      input: {
        slugs: postponedProductsIds,
        pagination: {
          limit: 12,
        },
      },
    },
    skip: !postponedProductsIds?.length,
  })

  const isFetching = isCatalogueFetching || isProductsFetching || !catalogueId || isPostponedProductsFetching

  const catalogueProducts = data?.products

  const products = useMemo(() => {
    if (isFetching) {
      return null
    }

    const result: typeof postponedProducts = []
    const ignoreCatalogueProductsIds = []

    // merge products preferring catalogue ones, because they have more info
    postponedProducts?.forEach((item) => {
      const catalogueProduct = catalogueProducts.find(({ id }) => id === item.id)

      if (catalogueProduct) {
        ignoreCatalogueProductsIds.push(item.id)
        result.push(catalogueProduct)
      }
      else {
        result.push(item)
      }
    })

    catalogueProducts?.forEach((item) => {
      if (!ignoreCatalogueProductsIds.includes(item.id)) {
        result.push(item)
      }
    })

    return result
      .filter((item) => item?.tradingItems?.items?.[0]?.section === 'Subscription')
      .slice(0, 12)
  }, [ isFetching, catalogueProducts, postponedProducts ])

  const handleContinue = () => {
    const { volume } = selectedItem.tradingItems?.items?.[0]

    track('First product change', {
      productId: selectedItem.id,
      productGender: selectedItem.gender,
      productFullName: selectedItem.fullName,
      productBrand: selectedItem.brandInfo.name,
      productCategory: selectedItem.category,
      productVolume: volume ? `${volume.volume} ${volume.unit}` : undefined,
    })

    onContinue(selectedItem)
    closeModal()
  }

  // if selected product is among products, we should pre-select it
  useEffect(() => {
    if (products) {
      const selectedProduct = products.find(({ tradingItems }) => tradingItems?.items?.[0].id === selectedTradingItemId)

      if (selectedProduct) {
        setSelectedItem(selectedProduct)
      }
    }
  }, [ products, selectedTradingItemId ])

  const buttonsClassName = cx(
    'sticky bottom-0 z-floating-header',
    s.buttons,
    isMobile
      ? 'bg-gold-30 pb-32'
      : `bg-gray-10 p-40`
  )

  return (
    <>
      <ModalHeadline
        title={messages.title}
        text={messages.text}
      />
      <div className="grid grid-cols-2 gap-16">
        {
          isFetching ? (
            new Array(6).fill(null).map((value, index) => (
              <ProductCardSmallSkeleton key={index} />
            ))
          ) : (
            products?.map((item) => (
              <ProductCardSmall
                key={item.id}
                data={item}
                withoutLink
                buttonProps={{
                  type: 'custom',
                  title: messages.try,
                  addedMessage: messages.selected,
                  isAdded: selectedItem === item,
                  onClick: () => {
                    setSelectedItem(item)
                  },
                }}
              />
            ))
          )
        }
      </div>
      <ModalButtons
        className={buttonsClassName}
        primary={{
          title: buttonMessages.continue,
          fullWidth: true,
          disabled: !selectedItem,
          onClick: handleContinue,
        }}
      />
    </>
  )
}

const FirstProductModal: React.FC<FirstProductModalProps & ModalComponentProps> = (props) => {
  const { name, closeModal, selectedTradingItemId, onContinue } = props
  const { isMobile } = useDevice()

  return (
    <PlainModal
      type={isMobile ? 'default' : 'sidebar-right'}
      closeModal={closeModal}
      data-testid={name}
    >
      <FirstProductModalView
        selectedTradingItemId={selectedTradingItemId}
        onContinue={onContinue}
        closeModal={closeModal}
      />
    </PlainModal>
  )
}


export default FirstProductModal
